import { todayDisplayDate, toDisplayDayjs } from "@/utils/displayTime";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type DatePreset =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "thisMonth"
  | "lastMonth"
  | "custom";

export const PRESET_LABELS: Record<DatePreset, string> = {
  today: "Today",
  yesterday: "Yesterday",
  last7: "Last 7 days",
  last30: "Last 30 days",
  thisMonth: "This month",
  lastMonth: "Last month",
  custom: "Custom range",
};

export function calcPresetRange(preset: Exclude<DatePreset, "custom">): {
  startDate: string;
  endDate: string;
} {
  const today = todayDisplayDate();
  const t = toDisplayDayjs();
  switch (preset) {
    case "today":
      return { startDate: today, endDate: today };
    case "yesterday": {
      const d = t.subtract(1, "day").format("YYYY-MM-DD");
      return { startDate: d, endDate: d };
    }
    case "last7":
      return {
        startDate: t.subtract(6, "day").format("YYYY-MM-DD"),
        endDate: today,
      };
    case "last30":
      return {
        startDate: t.subtract(29, "day").format("YYYY-MM-DD"),
        endDate: today,
      };
    case "thisMonth":
      return {
        startDate: t.startOf("month").format("YYYY-MM-DD"),
        endDate: today,
      };
    case "lastMonth": {
      const lm = t.subtract(1, "month");
      return {
        startDate: lm.startOf("month").format("YYYY-MM-DD"),
        endDate: lm.endOf("month").format("YYYY-MM-DD"),
      };
    }
  }
}

interface DateState {
  preset: DatePreset;
  startDate: string; // YYYY-MM-DD in the active display timezone
  endDate: string; // YYYY-MM-DD in the active display timezone
  setPreset: (preset: DatePreset) => void;
  setCustomRange: (start: string, end: string) => void;
}

export const useDateStore = create<DateState>()(
  persist(
    (set) => ({
      preset: "today" as DatePreset,
      startDate: todayDisplayDate(),
      endDate: todayDisplayDate(),
      setPreset: (preset) => {
        if (preset === "custom") {
          set({ preset });
        } else {
          set({ preset, ...calcPresetRange(preset) });
        }
      },
      setCustomRange: (startDate, endDate) => {
        set({ preset: "custom", startDate, endDate });
      },
    }),
    {
      name: "tracker-date",
      partialize: (s) => ({
        preset: s.preset,
        startDate: s.startDate,
        endDate: s.endDate,
      }),
      onRehydrateStorage: () => (state) => {
        // Recalculate non-custom presets on hydration so "Today" is always actual today
        if (state && state.preset !== "custom") {
          const range = calcPresetRange(
            state.preset as Exclude<DatePreset, "custom">,
          );
          state.startDate = range.startDate;
          state.endDate = range.endDate;
        }
      },
    },
  ),
);
